import { DeleteModelMessage, RemoveModelMessage, RemoveSubModelMessage, StartRoomTransitionMessage, RoomChangedMessage, convertToNumber3 } from "flavr-common";
import { Vector3, Quaternion } from "flavr-math";
import { ControllerMovedMessage, ControllerDisconnectedMessage } from "@messages/controller/ControllerMessages";
import { SetSelectionStrategyMessage, EnableControllerSelectionMessage, ClearSelectionForInputDeviceMessage, EnableModelSelectionMessage, SetRaycastSettingsMessage } from "@messages/selection/SelectionStrategyMessages";
import { ModelHoveredIn3DMessage, ModelHoveredInUIMessage } from "@messages/interaction/InteractionMessages";
import { SELECTION_STRATEGY } from "@messages/selection";
import { modelPool } from "@modules/model";
import { controllerDeviceService } from "@modules/inputs/controller";
import { ProximitySelect } from "./strategy/ProximitySelect";
import { IntenSelect } from "./strategy/IntenSelect";
import { RaySelect } from "./strategy/RaySelect";
export class SelectionHandler {
    constructor(connection, onSelect, onDeselect) {
        this.connection = connection;
        this.onSelect = onSelect;
        this.onDeselect = onDeselect;
        this.controllerData = new Map();
        this.lockedModels = new Set();
        this.lastHoveredModelUid = undefined;
        this.waitForRoomChange = false;
        this.enabled = true;
        this.raycastEnabled = true;
        this.raycastVisualizationEnabled = true;
        /// Message handlers ///
        this.handleControllerMovedMessage = (msg) => {
            const controller = this.controllerData.get(msg.uid);
            if (controller === undefined)
                return;
            controller.position.fromArray(msg.position);
            controller.orientation.fromArray(msg.orientation);
            controller.scale = msg.scale;
            this.updateSelectionForController(msg.uid, controller);
        };
        this.handleModelHoveredInUIMessage = (msg) => {
            var _a, _b;
            const controller = this.controllerData.get(msg.controllerUid);
            if (controller === undefined) {
                return;
            }
            if (!this.enabled || this.waitForRoomChange) {
                return;
            }
            if (controller.lockSelection || !controller.enabled) {
                return;
            }
            if (!controller.selectionStrategy.canSelectionBeUpdated()) {
                return;
            }
            if (this.lastHoveredModelUid !== undefined) {
                this.onDeselect(this.lastHoveredModelUid, msg.controllerUid, [0, 0, 0]);
                this.lastHoveredModelUid = undefined;
            }
            if (msg.modelUid !== undefined) {
                const pos = (_a = modelPool.getModel(msg.modelUid)) === null || _a === void 0 ? void 0 : _a.getPosition();
                if (pos !== undefined) {
                    this.onSelect((_b = msg.modelUid) !== null && _b !== void 0 ? _b : 0, msg.controllerUid, convertToNumber3(pos));
                    this.lastHoveredModelUid = msg.modelUid;
                }
            }
        };
        this.connection.subscribeByType(ControllerMovedMessage, this.handleControllerMovedMessage);
        this.connection.subscribeByType(ModelHoveredInUIMessage, this.handleModelHoveredInUIMessage);
        this.connection.subscribe([
            SetSelectionStrategyMessage.typestatic,
            DeleteModelMessage.typestatic,
            RemoveModelMessage.typestatic,
            RemoveSubModelMessage.typestatic,
            StartRoomTransitionMessage.typestatic,
            RoomChangedMessage.typestatic,
            ControllerDisconnectedMessage.typestatic,
            EnableControllerSelectionMessage.typestatic,
            EnableModelSelectionMessage.typestatic,
            ClearSelectionForInputDeviceMessage.typestatic,
            SetRaycastSettingsMessage.typestatic,
        ], (message) => {
            if (SetSelectionStrategyMessage.is(message)) {
                this.handleSetSelectionStrategyMessage(message);
            }
            else if (DeleteModelMessage.is(message) || RemoveModelMessage.is(message) || RemoveSubModelMessage.is(message)) {
                this.handleRemoveSubModelMessage(message);
            }
            else if (StartRoomTransitionMessage.is(message)) {
                this.handleStartRoomTransition();
            }
            else if (RoomChangedMessage.is(message)) {
                this.handleRoomChangedMessage();
            }
            else if (ControllerDisconnectedMessage.is(message)) {
                this.handleControllerDisconnectedMessage(message);
            }
            else if (EnableControllerSelectionMessage.is(message)) {
                this.handleEnableControllerSelectionMessage(message);
            }
            else if (EnableModelSelectionMessage.is(message)) {
                this.handleEnableModelSelectionMessage(message);
            }
            else if (ClearSelectionForInputDeviceMessage.is(message)) {
                this.handleClearSelectionForInputDeviceMessage(message);
            }
            else if (SetRaycastSettingsMessage.is(message)) {
                this.handleSetRaycastSettingsMessage(message);
            }
        });
        modelPool.setOnModelDestroyedHandler((model) => {
            this.handleRemoveSubModelMessage(new RemoveSubModelMessage(model.getUID()));
        });
    }
    init() { }
    /// Update controller selection ///
    getSelection(controllerUid) {
        const controller = this.controllerData.get(controllerUid);
        return (controller !== undefined) ? controller.selection : undefined;
    }
    lockSelection(controllerUid, lockSelection) {
        const controller = this.controllerData.get(controllerUid);
        if (controller === undefined)
            return;
        controller.lockSelection = lockSelection;
    }
    handleSetSelectionStrategyMessage(msg) {
        let strategy;
        switch (msg.strategy.type) {
            case SELECTION_STRATEGY.RAY_SELECT:
                strategy = new RaySelect(this.connection, msg.controllerUid, msg.selectionOrigin, msg.selectionRotation, msg.strategy);
                break;
            case SELECTION_STRATEGY.INTEN_SELECT:
                strategy = new IntenSelect(this.connection, msg.controllerUid, msg.strategy);
                break;
            case SELECTION_STRATEGY.PROXIMITY_SELECT:
                strategy = new ProximitySelect(this.connection, msg.controllerUid, msg.strategy);
                break;
            default:
                return;
        }
        if (strategy instanceof RaySelect) {
            const isMouseController = msg.controllerUid === controllerDeviceService.mouseDeviceUID;
            strategy.setRayVisible(!isMouseController && this.raycastVisualizationEnabled);
        }
        if (!this.enabled && strategy !== undefined) {
            strategy.hide();
        }
        const controller = this.controllerData.get(msg.controllerUid);
        if (controller === undefined) {
            this.controllerData.set(msg.controllerUid, {
                enabled: true,
                selectionStrategy: strategy,
                selection: undefined,
                lockSelection: false,
                disconnected: false,
                position: new Vector3(),
                orientation: new Quaternion(),
                scale: 1
            });
        }
        else {
            controller.selectionStrategy.hide();
            controller.selectionStrategy = strategy;
            if (this.enabled && controller.enabled) {
                strategy.show();
            }
        }
    }
    /// Selection helper methods ///
    handleSelectionUpdate(controllerUID, controller, newSelection) {
        if (!controller.enabled || controller.lockSelection || controller.disconnected)
            return;
        if (!this.raycastEnabled && controller.selectionStrategy instanceof RaySelect)
            return;
        const oldSelection = controller.selection;
        newSelection = (newSelection !== undefined && this.lockedModels.has(newSelection.uid)) ? undefined : newSelection;
        controller.selection = newSelection;
        const newModel = (newSelection !== undefined) ? newSelection.uid : undefined;
        const oldModel = (oldSelection !== undefined) ? oldSelection.uid : undefined;
        if (newModel !== oldModel) {
            if (oldSelection !== undefined) {
                this.onDeselect(oldSelection.uid, controllerUID, oldSelection.hitPoint);
                this.lastHoveredModelUid = undefined;
                this.connection.send(new ModelHoveredIn3DMessage(undefined));
            }
            if (newSelection !== undefined) {
                this.onSelect(newSelection.uid, controllerUID, newSelection.hitPoint);
                this.lastHoveredModelUid = newSelection.uid;
                this.connection.send(new ModelHoveredIn3DMessage(newSelection.uid));
            }
        }
    }
    handleEnableControllerSelectionMessage(msg) {
        if (msg.uid !== undefined) {
            const data = this.controllerData.get(msg.uid);
            if (data === undefined)
                return;
            data.enabled = msg.enabled;
            this.onEnabledController(msg.uid, data, msg.enabled);
            return;
        }
        this.enabled = msg.enabled;
        for (const [uid, data] of this.controllerData) {
            this.onEnabledController(uid, data, msg.enabled);
        }
    }
    handleEnableModelSelectionMessage(msg) {
        if (msg.enabled) {
            this.lockedModels.delete(msg.uid);
            return;
        }
        this.lockedModels.add(msg.uid);
        for (const [uid, controller] of this.controllerData) {
            const selection = controller.selection;
            if ((selection === null || selection === void 0 ? void 0 : selection.uid) !== msg.uid)
                continue;
            this.onDeselect(msg.uid, uid, selection.hitPoint);
            controller.selection = undefined;
        }
    }
    handleSetRaycastSettingsMessage(msg) {
        const wasRaycastEnabled = this.raycastEnabled;
        this.raycastEnabled = msg.raycastEnabled;
        this.raycastVisualizationEnabled = msg.visualizationEnabled;
        for (const [uid, controller] of this.controllerData) {
            if (!(controller.selectionStrategy instanceof RaySelect))
                continue;
            const isMouseController = uid === controllerDeviceService.mouseDeviceUID;
            controller.selectionStrategy.setRayVisible(!isMouseController && this.raycastVisualizationEnabled);
            if (wasRaycastEnabled && !this.raycastEnabled && controller.selection !== undefined) {
                this.onDeselect(controller.selection.uid, uid, controller.selection.hitPoint);
                controller.selection = undefined;
                if (this.lastHoveredModelUid !== undefined) {
                    this.lastHoveredModelUid = undefined;
                    this.connection.send(new ModelHoveredIn3DMessage(undefined));
                }
            }
        }
    }
    updateSelectionForController(controllerUid, controller) {
        if (!this.enabled || this.waitForRoomChange)
            return;
        if (controller.lockSelection || !controller.enabled)
            return;
        if (!this.raycastEnabled && controller.selectionStrategy instanceof RaySelect)
            return;
        if (!controller.selectionStrategy.canSelectionBeUpdated())
            return;
        controller.selectionStrategy.updateSelection(controller.position, controller.orientation, controller.scale, (selection) => {
            this.handleSelectionUpdate(controllerUid, controller, selection);
        });
    }
    handleClearSelectionForInputDeviceMessage(msg) {
        const controller = this.controllerData.get(msg.controllerUid);
        if ((controller === null || controller === void 0 ? void 0 : controller.selection) !== undefined) {
            this.onDeselect(controller.selection.uid, msg.controllerUid, controller.selection.hitPoint);
            controller.selection = undefined;
        }
    }
    /// Room and model helper methods ///
    handleStartRoomTransition() {
        // we are transitioning into a new room
        // -> clear selection and disable selection until we have reached the new room
        for (const [uid, controller] of this.controllerData) {
            if (controller.selection !== undefined) {
                if (controller.selection.uid !== undefined) {
                    this.onDeselect(controller.selection.uid, uid, controller.selection.hitPoint);
                }
                controller.selection = undefined;
            }
            controller.selectionStrategy.hide();
            controller.lockSelection = false;
        }
        this.waitForRoomChange = true;
    }
    handleRoomChangedMessage() {
        // we have reached a new room -> re-enable selection
        this.waitForRoomChange = false;
        for (const controller of this.controllerData.values()) {
            controller.selectionStrategy.show();
        }
    }
    handleRemoveSubModelMessage(msg) {
        for (const [uid, controller] of this.controllerData) {
            const selection = controller.selection;
            if (selection !== undefined && selection.uid === msg.uid) {
                this.onDeselect(msg.uid, uid, selection.hitPoint);
                controller.selection = undefined;
            }
        }
        if (this.lastHoveredModelUid === msg.uid) {
            this.lastHoveredModelUid = undefined;
            this.connection.send(new ModelHoveredIn3DMessage(undefined));
        }
    }
    /// Controller helper methods ///
    onEnabledController(uid, data, enabled) {
        if (enabled) {
            data.selectionStrategy.show();
            data.selectionStrategy.updateSelection(data.position, data.orientation, data.scale, (selection) => {
                this.handleSelectionUpdate(uid, data, selection);
            });
        }
        else {
            if (data.selection !== undefined) {
                this.onDeselect(data.selection.uid, uid, data.selection.hitPoint);
                data.selection = undefined;
            }
            data.selectionStrategy.hide();
        }
    }
    handleControllerDisconnectedMessage(msg) {
        const controller = this.controllerData.get(msg.uid);
        if (controller === undefined) {
            return;
        }
        controller.disconnected = true;
        if (controller.selection !== undefined) {
            this.onDeselect(controller.selection.uid, msg.uid, controller.selection.hitPoint);
        }
        this.controllerData.delete(msg.uid);
    }
}
